import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const staggerDelays = ["0ms", "40ms", "80ms", "120ms", "160ms", "200ms", "240ms", "280ms"];

export default function MobileBottomNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [itemsVisible, setItemsVisible] = useState(false);

  const mainTabs = [
    { labelKey: "mobile.home",        icon: "ri-home-5-line",       activeIcon: "ri-home-5-fill",       path: "/" },
    { labelKey: "mobile.stories",     icon: "ri-trophy-line",        activeIcon: "ri-trophy-fill",        path: "/success-stories" },
    { labelKey: "nav.interviews",     icon: "ri-play-circle-line",   activeIcon: "ri-play-circle-fill",   path: "/interviews" },
    { labelKey: "mobile.opportunites",icon: "ri-compass-3-line",     activeIcon: "ri-compass-3-fill",     path: "/opportunites" },
    { labelKey: "mobile.more",        icon: "ri-grid-line",          activeIcon: "ri-grid-fill",          path: "__more__" },
  ];

  const moreMenuItems = [
    { labelKey: "nav.startups",       icon: "ri-rocket-line",         path: "/startups",           color: "text-green-600",  bg: "bg-green-50"  },
    { labelKey: "nav.innovations",    icon: "ri-lightbulb-line",      path: "/innovations",        color: "text-yellow-600", bg: "bg-yellow-50" },
    { labelKey: "nav.femmes",         icon: "ri-women-line",          path: "/femmes-entrepreneures", color: "text-pink-500", bg: "bg-pink-50"   },
    { labelKey: "nav.jeunesTalents",  icon: "ri-star-line",           path: "/jeunes-talents",     color: "text-purple-500", bg: "bg-purple-50" },
    { labelKey: "nav.diaspora",       icon: "ri-earth-line",          path: "/diaspora-africaine", color: "text-teal-600",   bg: "bg-teal-50"   },
    { labelKey: "nav.formations",     icon: "ri-graduation-cap-line", path: "/formations",         color: "text-orange-500", bg: "bg-orange-50" },
    { labelKey: "mobile.search",      icon: "ri-search-line",         path: "/recherche",          color: "text-gray-500",   bg: "bg-gray-100"  },
    { labelKey: "nav.apropos",        icon: "ri-information-line",    path: "/a-propos",           color: "text-gray-500",   bg: "bg-gray-100"  },
  ];

  useEffect(() => {
    if (moreOpen) {
      setOverlayVisible(true);
      const t1 = setTimeout(() => setPanelVisible(true), 10);
      const t2 = setTimeout(() => setItemsVisible(true), 80);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } else {
      setItemsVisible(false);
      setPanelVisible(false);
      const t = setTimeout(() => setOverlayVisible(false), 350);
      return () => clearTimeout(t);
    }
  }, [moreOpen]);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isMoreActive = moreMenuItems.some((item) => location.pathname.startsWith(item.path));

  return (
    <>
      {/* Overlay */}
      {overlayVisible && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{
            backgroundColor: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(4px)",
            transition: "opacity 280ms ease",
            opacity: panelVisible ? 1 : 0,
          }}
          onClick={() => setMoreOpen(false)}
        />
      )}

      {/* Explorer panel */}
      {overlayVisible && (
        <div
          className="fixed left-0 right-0 bottom-16 z-50 lg:hidden"
          style={{
            transition: "transform 320ms cubic-bezier(0.32, 0.72, 0, 1), opacity 280ms ease",
            transform: panelVisible ? "translateY(0)" : "translateY(100%)",
            opacity: panelVisible ? 1 : 0,
          }}
        >
          <div className="mx-2 bg-white rounded-t-3xl overflow-hidden" style={{ boxShadow: "0 -12px 48px rgba(0,0,0,0.14)" }}>
            <div className="flex justify-center pt-3 pb-1">
              <div className="rounded-full bg-gray-200" style={{ width: "36px", height: "4px" }}></div>
            </div>

            <div className="flex items-center justify-between px-5 py-3">
              <div>
                <p className="text-sm font-bold text-gray-900">{t("mobile.more")}</p>
                <p className="text-xs text-gray-400 mt-0.5">{t("categories.title")}</p>
              </div>
              <button
                onClick={() => setMoreOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>

            <div className="grid grid-cols-4 gap-1.5 px-4 pb-4">
              {moreMenuItems.map((item, i) => {
                const active = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMoreOpen(false)}
                    className={`flex flex-col items-center gap-1.5 py-3 px-1 rounded-2xl cursor-pointer ${
                      active ? "bg-orange-50 border border-orange-200" : "bg-gray-50 hover:bg-gray-100"
                    }`}
                    style={{
                      transition: `opacity 240ms ease ${staggerDelays[i]}, transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1) ${staggerDelays[i]}`,
                      opacity: itemsVisible ? 1 : 0,
                      transform: itemsVisible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.92)",
                    }}
                  >
                    <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${item.bg}`}>
                      <i className={`${item.icon} text-xl ${active ? "text-orange-500" : item.color}`}></i>
                    </div>
                    <span className={`text-[11px] font-semibold text-center leading-tight ${active ? "text-orange-500" : "text-gray-700"}`}>
                      {t(item.labelKey)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bottom nav bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white border-t border-gray-100"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex items-stretch justify-around h-16 px-1">
          {mainTabs.map((tab) => {
            const isMore = tab.path === "__more__";
            const active = isMore ? isMoreActive || moreOpen : isActive(tab.path);

            if (isMore) {
              return (
                <button
                  key="more"
                  onClick={() => setMoreOpen((v) => !v)}
                  className="relative flex flex-col items-center justify-center gap-1 flex-1 py-2 cursor-pointer"
                >
                  {(active || moreOpen) && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 rounded-full bg-orange-500" style={{ width: "24px", height: "3px" }} />
                  )}
                  <div className={`w-7 h-7 flex items-center justify-center rounded-xl transition-all duration-200 ${
                    moreOpen ? "bg-orange-500 text-white scale-110" : active ? "text-orange-500" : "text-gray-400"
                  }`}>
                    <i
                      className={`text-xl transition-transform duration-300 ${moreOpen ? "ri-close-line" : "ri-grid-fill"}`}
                      style={{ transform: moreOpen ? "rotate(90deg)" : "rotate(0deg)" }}
                    ></i>
                  </div>
                  <span className={`text-[10px] font-semibold transition-colors duration-200 ${active || moreOpen ? "text-orange-500" : "text-gray-400"}`}>
                    {moreOpen ? t("listing.hideFilters") : t(tab.labelKey)}
                  </span>
                </button>
              );
            }

            return (
              <Link
                key={tab.path}
                to={tab.path}
                className="relative flex flex-col items-center justify-center gap-1 flex-1 py-2 cursor-pointer"
                onClick={() => setMoreOpen(false)}
              >
                {active && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 rounded-full bg-orange-500" style={{ width: "24px", height: "3px" }} />
                )}
                <div className={`w-7 h-7 flex items-center justify-center rounded-xl transition-all duration-200 ${
                  active ? "bg-orange-50 text-orange-500 scale-110" : "text-gray-400"
                }`}>
                  <i className={`${active ? tab.activeIcon : tab.icon} text-xl`}></i>
                </div>
                <span className={`text-[10px] font-semibold transition-colors duration-200 ${active ? "text-orange-500" : "text-gray-400"}`}>
                  {t(tab.labelKey)}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      <div className="h-16 lg:hidden" aria-hidden="true"></div>
    </>
  );
}
